import { useTranslation } from 'react-i18next';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import type { DocsOperation } from '../../generated/reference';
import { CodeBlock } from '../code-block';
import { SchemaViewer } from '../schema-viewer';

import { exampleCode, isErrorStatus } from './endpoint-example';
import { EndpointSection } from './endpoint-section';
import { StatusCode } from './status-code';

/** A tab per success status: description, schema and the recorded example. */
export function EndpointResponses({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const statuses = Object.keys(operation.responses)
    .filter((status) => !isErrorStatus(status))
    .sort();
  const first = statuses[0];
  if (!first) return null;

  return (
    <EndpointSection id="responses" title={t('endpoint.responses.title')}>
      <Tabs defaultValue={first} className="gap-3">
        <TabsList aria-label={t('endpoint.responses.tabs')}>
          {statuses.map((status) => (
            <TabsTrigger key={status} value={status} className="font-mono">
              {status}
            </TabsTrigger>
          ))}
        </TabsList>
        {statuses.map((status) => {
          const response = operation.responses[status];
          if (!response) return null;
          const example = response.example ?? operation.examples.responses[status];
          return (
            <TabsContent key={status} value={status} className="flex flex-col gap-4">
              <p className="flex items-center gap-2 text-sm">
                <StatusCode status={status} />
                <span>{response.description}</span>
              </p>
              {response.body === 'empty' ? (
                <p className="text-muted-foreground text-sm">{t('endpoint.responses.noBody')}</p>
              ) : (
                <>
                  {response.schema ? <SchemaViewer schema={response.schema} /> : null}
                  {example !== undefined ? (
                    <CodeBlock {...exampleCode(example)} label={t('endpoint.responses.example')} />
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {t('endpoint.responses.noExample')}
                    </p>
                  )}
                </>
              )}
            </TabsContent>
          );
        })}
      </Tabs>
    </EndpointSection>
  );
}
